declare global {
  interface Window {
    google?: {
      maps: {
        importLibrary: (name: string) => Promise<unknown>;
        places?: {
          Autocomplete: new (
            input: HTMLInputElement,
            options?: Record<string, unknown>
          ) => {
            addListener: (event: string, handler: () => void) => void;
            getPlace: () => { formatted_address?: string };
          };
          PlaceAutocompleteElement: new (
            options?: Record<string, unknown>
          ) => HTMLElement & {
            placeholder: string;
            addEventListener: (type: string, listener: (event: unknown) => void) => void;
            removeEventListener: (type: string, listener: (event: unknown) => void) => void;
            inputElement?: HTMLInputElement | null;
          };
        };
      };
    };
  }
}

const SCRIPT_ID = 'google-maps-script';
const LOAD_TIMEOUT_MS = 15000;

type PlacesLibrary = {
  Autocomplete?: new (
    input: HTMLInputElement,
    options?: Record<string, unknown>
  ) => {
    addListener: (event: string, handler: () => void) => void;
    getPlace: () => { formatted_address?: string };
  };
  PlaceAutocompleteElement?: new (
    options?: Record<string, unknown>
  ) => HTMLElement & {
    placeholder: string;
    addEventListener: (type: string, listener: (event: unknown) => void) => void;
    removeEventListener: (type: string, listener: (event: unknown) => void) => void;
  };
};

function waitForGoogleMapsImportLibrary(timeoutMs = LOAD_TIMEOUT_MS): Promise<void> {
  if (window.google?.maps?.importLibrary) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;
    const timer = window.setInterval(() => {
      if (window.google?.maps?.importLibrary) {
        window.clearInterval(timer);
        resolve();
      } else if (Date.now() > deadline) {
        window.clearInterval(timer);
        reject(new Error('Google Maps importLibrary timed out'));
      }
    }, 50);
  });
}

export async function getGoogleMapsApiKey(): Promise<string | null> {
  const fromEnv = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim();
  if (fromEnv) return fromEnv;

  try {
    const response = await fetch('/api/public-config');
    if (!response.ok) return null;
    const data = await response.json();
    const key = data.googleMapsApiKey?.trim();
    return key || null;
  } catch {
    return null;
  }
}

export function loadGoogleMapsScript(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const finish = () => {
      waitForGoogleMapsImportLibrary()
        .then(resolve)
        .catch(reject);
    };

    if (window.google?.maps?.importLibrary) {
      finish();
      return;
    }

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', () => reject(new Error('Google Maps script failed to load')), { once: true });
      finish();
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async`;
    script.async = true;
    script.onload = finish;
    script.onerror = () => reject(new Error('Google Maps script failed to load'));
    document.head.appendChild(script);
  });
}

async function loadPlacesLibrary(): Promise<PlacesLibrary> {
  await waitForGoogleMapsImportLibrary();
  return (await window.google!.maps.importLibrary('places')) as PlacesLibrary;
}

type PlaceAutocompleteElementInstance = HTMLElement & {
  placeholder: string;
  value: string;
  inputElement?: HTMLInputElement | null;
  addEventListener: (type: string, listener: (event: unknown) => void) => void;
  removeEventListener: (type: string, listener: (event: unknown) => void) => void;
};

function getPlaceAutocompleteElement(host: HTMLElement): PlaceAutocompleteElementInstance | null {
  const pac = host.querySelector('gmp-place-autocomplete');
  return pac instanceof HTMLElement ? (pac as PlaceAutocompleteElementInstance) : null;
}

async function tryNewAutocomplete(
  host: HTMLElement,
  placeholder: string,
  onSelect: (address: string) => void,
  onInputChange?: (address: string) => void,
  initialValue?: string
): Promise<(() => void) | null> {
  const placesLibrary = await loadPlacesLibrary();
  const PlaceAutocompleteElement = placesLibrary.PlaceAutocompleteElement;
  if (!PlaceAutocompleteElement) return null;

  const pac = new PlaceAutocompleteElement({
    includedRegionCodes: ['us'],
  }) as PlaceAutocompleteElementInstance;
  pac.placeholder = placeholder;
  if (initialValue) {
    pac.value = initialValue;
  }
  host.appendChild(pac);

  const syncInputValue = () => {
    const current = pac.value?.trim() || getAutocompleteValue(host);
    if (current) onInputChange?.(current);
  };

  const handlePacInput = () => syncInputValue();
  pac.addEventListener('input', handlePacInput);
  pac.addEventListener('change', handlePacInput);

  const attachInputListener = () => {
    const input = findAutocompleteInput(host);
    if (!input || input.dataset.addressListenerAttached) return null;

    const handleInput = () => syncInputValue();
    input.addEventListener('input', handleInput);
    input.addEventListener('change', handleInput);
    input.dataset.addressListenerAttached = 'true';
    return () => {
      input.removeEventListener('input', handleInput);
      input.removeEventListener('change', handleInput);
      delete input.dataset.addressListenerAttached;
    };
  };

  let detachInputListener = attachInputListener();
  const inputAttachTimer = window.setInterval(() => {
    if (!detachInputListener) {
      detachInputListener = attachInputListener();
    }
    if (detachInputListener) {
      window.clearInterval(inputAttachTimer);
    }
  }, 100);

  const handler = async (event: unknown) => {
    // Sync visible text immediately — fetchFields is async and submit may happen first
    syncInputValue();

    const { placePrediction } = event as {
      placePrediction?: {
        toPlace: () => { fetchFields: (opts: { fields: string[] }) => Promise<void>; formattedAddress?: string };
      };
    };
    if (!placePrediction) return;

    const place = placePrediction.toPlace();
    await place.fetchFields({ fields: ['formattedAddress'] });
    if (place.formattedAddress) {
      onSelect(place.formattedAddress);
    } else {
      syncInputValue();
    }
  };

  pac.addEventListener('gmp-select', handler);

  return () => {
    window.clearInterval(inputAttachTimer);
    detachInputListener?.();
    pac.removeEventListener('input', handlePacInput);
    pac.removeEventListener('change', handlePacInput);
    pac.removeEventListener('gmp-select', handler);
    pac.remove();
  };
}

async function tryLegacyAutocomplete(
  host: HTMLElement,
  placeholder: string,
  onSelect: (address: string) => void,
  onInputChange?: (address: string) => void
): Promise<(() => void) | null> {
  const placesLibrary = await loadPlacesLibrary();
  const Autocomplete = placesLibrary.Autocomplete;
  if (!Autocomplete) return null;

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = placeholder;
  input.autocomplete = 'off';
  input.className =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary';
  host.appendChild(input);

  const handleInput = () => {
    if (input.value.trim()) onInputChange?.(input.value.trim());
  };
  input.addEventListener('input', handleInput);
  input.addEventListener('change', handleInput);

  const autocomplete = new Autocomplete(input, {
    types: ['address'],
    componentRestrictions: { country: 'us' },
    fields: ['formatted_address'],
  });

  autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace();
    if (place?.formatted_address) {
      onSelect(place.formatted_address);
    } else {
      handleInput();
    }
  });

  return () => {
    input.removeEventListener('input', handleInput);
    input.removeEventListener('change', handleInput);
    input.remove();
  };
}

export function findAutocompleteInput(host: HTMLElement): HTMLInputElement | null {
  const pac = getPlaceAutocompleteElement(host);
  if (pac?.inputElement instanceof HTMLInputElement) {
    return pac.inputElement;
  }

  if (pac) {
    // Closed shadow root — inputElement may be unavailable; value is on the element itself.
    return null;
  }

  const input = host.querySelector('input');
  return input instanceof HTMLInputElement ? input : null;
}

export function getAutocompleteValue(host: HTMLElement): string {
  const pac = getPlaceAutocompleteElement(host);
  if (pac?.value?.trim()) {
    return pac.value.trim();
  }
  return findAutocompleteInput(host)?.value.trim() || '';
}

export async function mountAddressAutocomplete(
  host: HTMLElement,
  options: {
    placeholder?: string;
    initialValue?: string;
    onSelect: (address: string) => void;
    onInputChange?: (address: string) => void;
  }
): Promise<() => void> {
  const apiKey = await getGoogleMapsApiKey();
  if (!apiKey) {
    throw new Error('Google Maps API key is not configured');
  }

  await loadGoogleMapsScript(apiKey);

  const placeholder = options.placeholder || '123 Main Street, City, State 12345';
  const cleanupNew = await tryNewAutocomplete(
    host,
    placeholder,
    options.onSelect,
    options.onInputChange,
    options.initialValue
  );
  if (cleanupNew) return cleanupNew;

  const cleanupLegacy = await tryLegacyAutocomplete(
    host,
    placeholder,
    options.onSelect,
    options.onInputChange
  );
  if (cleanupLegacy) {
    if (options.initialValue) {
      syncAutocompleteValue(host, options.initialValue);
    }
    return cleanupLegacy;
  }

  throw new Error('Google Places Autocomplete is unavailable');
}

export function syncAutocompleteValue(host: HTMLElement, value: string) {
  if (!value) return;

  const pac = getPlaceAutocompleteElement(host);
  if (pac) {
    pac.value = value;
    return;
  }

  const input = findAutocompleteInput(host);
  if (input && input.value !== value) {
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

export async function syncAutocompleteValueWhenReady(
  host: HTMLElement,
  value: string,
  maxWaitMs = 5000
): Promise<void> {
  if (!value) return;

  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    const pac = getPlaceAutocompleteElement(host);
    if (pac) {
      pac.value = value;
      return;
    }

    const input = findAutocompleteInput(host);
    if (input) {
      syncAutocompleteValue(host, value);
      return;
    }

    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }
}
